import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Briefcase, Loader2, ArrowLeft, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import Footer from '@/components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffLogin() {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);

  // Get tenant code from URL path
  const getTenantCode = () => {
    const pathParts = window.location.pathname.split('/');
    // URL pattern: /tenant-code/staff-portal or just /staff-portal
    if (pathParts.length > 2 && pathParts[2] === 'staff-portal') {
      return pathParts[1];
    }
    // Check if first part is a tenant code (not 'staff-portal')
    if (pathParts[1] && pathParts[1] !== 'staff-portal') {
      return pathParts[1];
    }
    // Try from localStorage
    return localStorage.getItem('tenant_code') || null;
  };

  const tenantCode = getTenantCode();

  useEffect(() => {
    if (tenantCode) {
      fetchTenantInfo();
    } else {
      setTenantLoading(false);
    }
  }, [tenantCode]);

  const fetchTenantInfo = async () => {
    try {
      setTenantLoading(true);
      const response = await fetch(`${API_URL}/api/tenants/by-subdomain/${tenantCode}`);
      if (response.ok) {
        const data = await response.json();
        setTenant(data);
      }
    } catch (err) {
      console.error('Error fetching tenant info:', err);
    } finally {
      setTenantLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!employeeId || !password) {
      toast.error('Please enter Employee ID and Password');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/staff-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          employee_id: employeeId.toUpperCase(), 
          password 
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('staffToken', data.access_token);
        localStorage.setItem('staffEmployeeId', data.employee_id);
        localStorage.setItem('staffName', data.full_name);
        localStorage.setItem('staffRole', data.role);
        if (tenantCode) {
          localStorage.setItem('staff_tenant_code', tenantCode);
        }
        
        toast.success(`Welcome, ${data.full_name || data.employee_id}!`);
        
        if (data.must_change_password) {
          navigate('/staff-portal/change-password');
        } else {
          navigate('/staff-portal/dashboard');
        }
      } else {
        toast.error(data.detail || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    if (tenantCode) {
      window.location.href = `/${tenantCode}`;
    } else {
      window.location.href = '/';
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto" />
          <p className="mt-4 text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 flex items-center justify-center p-4 relative">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center pb-1 pt-5">
          {/* Tenant Logo & Name */}
          {tenant ? (
            <>
              {tenant.logo_url ? (
                <img 
                  src={tenant.logo_url} 
                  alt={tenant.name} 
                  className="h-12 w-auto object-contain mx-auto mb-2"
                />
              ) : (
                <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Building2 className="h-7 w-7 text-indigo-600" />
                </div>
              )}
              <h2 className="text-base font-semibold text-gray-700 mb-1">{tenant.name}</h2>
            </>
          ) : (
            <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Briefcase className="h-7 w-7 text-indigo-600" />
            </div>
          )}
          <CardTitle className="text-xl font-bold text-gray-900">Staff Portal</CardTitle>
          <p className="text-gray-500 text-sm mt-1">Login with your Employee ID</p>
        </CardHeader>
        <CardContent className="pt-2 pb-5">
          <form onSubmit={handleLogin} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Employee ID</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="e.g., ILOEEMP0001"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                  className="pl-10 h-10 uppercase"
                  data-testid="staff-employee-id-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-10"
                  data-testid="staff-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-10 bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
              data-testid="staff-login-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="animate-spin h-5 w-5" />
                  Logging in...
                </div>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <p className="text-sm text-gray-500">
              Forgot password? Contact your administrator
            </p>
            <button 
              onClick={handleBackToHome}
              className="text-sm text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </CardContent>
      </Card>
      
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0">
        <Footer variant="minimal" className="text-gray-300" />
      </div>
    </div>
  );
}
